import {
  ColumnDef,
  PaginationState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataGrid } from '@/components/DataGrid';
import { DEFAULT_10_PAGINATION_MODEL } from '@/constants/dataGridConstants';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { usePromptOptimizationRuns } from '@/hooks/useSwr';
import { PromptOptimizationJob } from '@/types/schema';

import { StatusChip } from './Chip';
import { formatDate } from './utils';

export const OptimizationRunsTable = ({ repoName }: { repoName: string }) => {
  'use no memo';
  const { data: optimizationRuns, isLoading } =
    usePromptOptimizationRuns(repoName);

  const [, setSearchParams] = useSearchParams();

  const [paginationState, setPaginationState] = useStateFromSearchParams(
    'paginationState',
    JSON.stringify(DEFAULT_10_PAGINATION_MODEL)
  );

  let pagination = DEFAULT_10_PAGINATION_MODEL;
  try {
    pagination = JSON.parse(paginationState ?? '') as PaginationState;
  } catch (e) {
    // ignore: use default
  }

  const setPagination = useCallback(
    (paginationUpdater) => {
      const newPagination = paginationUpdater(pagination);
      setPaginationState(JSON.stringify(newPagination));
    },
    [pagination, setPaginationState]
  );

  const columnHelper = createColumnHelper<PromptOptimizationJob>();
  const DEFAULT_COLUMNS: ColumnDef<PromptOptimizationJob, any>[] = [
    columnHelper.accessor('id', {
      id: 'job-id',
      enableResizing: false,
      size: 30,
      header: () => <div>Job ID</div>,
      enableSorting: false,
    }),
    columnHelper.accessor('status', {
      id: 'job-status',
      enableResizing: false,
      size: 10,
      header: () => <div>Status</div>,
      cell: ({ row }) => <StatusChip status={row.original.status} />,
      enableSorting: false,
    }),
    columnHelper.accessor('algorithm', {
      id: 'job-algorithm',
      enableResizing: false,
      size: 20,
      enableSorting: false,
      header: () => <div>Algorithm</div>,
    }),
    columnHelper.accessor('config.dataset_name', {
      enableResizing: false,
      size: 20,
      enableSorting: false,
      header: () => <div>Dataset</div>,
    }),
    columnHelper.accessor('created_at', {
      id: 'job-created-at',
      enableResizing: false,
      size: 20,
      header: () => <div>Created at</div>,
      cell: ({ row }) => (
        <div className="text-xs">{formatDate(row.original.created_at)}</div>
      ),
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    columns: DEFAULT_COLUMNS,
    data: optimizationRuns ?? [],
    state: {
      pagination,
    },

    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    columnResizeMode: 'onChange',

    enableSorting: true,
    enableExpanding: true,
    enableColumnFilters: true,

    manualExpanding: true,
    manualFiltering: false,
    manualSorting: true,
    manualPagination: true,

    pageCount: Math.ceil((optimizationRuns?.length || 1) / pagination.pageSize),
  });

  const onRowClick = (row) => {
    setSearchParams(
      (prev) => {
        prev.set('jobId', row.id);
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <div className="rounded-md border">
      <DataGrid
        table={table}
        isLoading={isLoading}
        cellHeight={44}
        onClick={onRowClick}
        emptyState={<div>No optimization runs found</div>}
        maxWidthFull={true}
      />
    </div>
  );
};
